/**
 * Datum en tijd kiezen voor een geplande Mass Message, in Amsterdamse tijd.
 *
 * 04-10-2026 (Jordy): in een slot van Mass Messages → Create staan datum en
 * tijd nooit leeg en nooit op een oude datum. Een nieuw, gewist of net
 * gepland slot begint op "nu", naar boven afgerond op de volgende 5 minuten.
 * Daarnaast snelknoppen ("Now", "+15 min", "+30 min", "+1 hour") en een
 * keuzelijst met tijden per kwartier. Vrij typen blijft gewoon kunnen (ook op
 * 5 minuten of op de minuut nauwkeurig).
 *
 * Alles rekent in echte momenten (UTC-milliseconden) en zet pas aan het eind
 * om naar een Amsterdamse kalenderdag en tijd. Amsterdam ligt een heel aantal
 * uren voor op UTC, dus afronden op 5 minuten in UTC is hetzelfde als afronden
 * in Amsterdam, en optellen over een klokwissel heen klopt vanzelf (01:45 +
 * 30 min op de ochtend van de zomertijd is 03:15, niet 02:15).
 *
 * Dit bestand heeft geen React en geen browser nodig; de tests voeren het uit
 * met node (tests/test_massa_create.py, in de tijdzone New York).
 *
 * 07-10-2026 (Jordy): elke functie neemt als laatste een tijdzone (standaard
 * Europe/Amsterdam, dus zonder zone alles precies zoals vroeger). Met
 * Asia/Manila zijn datum en tijd die van Manila; het moment in UTC is echt.
 * Afronden op 5 minuten in UTC blijft gelijk aan afronden in de zone, want
 * elke zone wijkt een veelvoud van 5 minuten af van UTC.
 */
import { AMSTERDAM, zoneDelen, zoneNaarUtc } from './amsterdam'

/** Zelfde grenzen als de server (`_parse_schedule_at_or_400`). */
export const MIN_VOORUIT_MS = 2 * 60_000
export const MAX_VOORUIT_MS = 90 * 86_400_000
const STAP_MS = 5 * 60_000

export interface DatumTijd {
  datum: string
  tijd: string
}

function delenVan(ms: number, zone: string = AMSTERDAM): DatumTijd {
  return zoneDelen(new Date(ms).toISOString(), zone) as DatumTijd
}

function naarBovenOp5(ms: number): number {
  return Math.ceil(ms / STAP_MS) * STAP_MS
}

/**
 * Het standaardmoment: nu, naar boven afgerond op de volgende 5 minuten.
 *
 * De server wil minstens 2 minuten vooruit. Zou de afronding daaronder
 * blijven (om 12:03:30 is 12:05 maar anderhalve minuut weg), dan wordt het
 * het 5-minutenmoment daarna (12:10). Zo is de standaard altijd meteen te
 * plannen.
 */
export function standaardMoment(nu: number = Date.now(), zone: string = AMSTERDAM): DatumTijd {
  // +1 ms: precies 2 minuten vooruit is op het moment van versturen al te kort.
  return delenVan(naarBovenOp5(nu + MIN_VOORUIT_MS + 1), zone)
}

/** 06-10-2026 (Jordy): de standaardtijd van een NIEUW Mass Message ligt zoveel
 *  minuten vooruit (Create bij openen/verversen/Clear/na plannen, en Mass DM
 *  via Chats). Eén plek; "Now" en de snelknoppen blijven zoals ze zijn. */
export const STANDAARD_VOORUIT_MINUTEN = 15

/** De melding bij een slot waarvan de tijd voorbij is (of binnen 2 minuten). */
export const TIJD_VOORBIJ = 'Time has passed — pick a new time'

/**
 * Standaard voor een nieuw Mass Message: nu + 15 minuten (Amsterdam), naar
 * boven afgerond op de volgende stap van 5 minuten. Er wordt eerst op de
 * minuut afgerond, zodat 09:35 (ook 09:35:40) 09:50 wordt en 09:37 09:55;
 * 23:50 wordt 00:05 op de volgende dag.
 */
export function standaardNieuwMoment(nu: number = Date.now(), zone: string = AMSTERDAM): DatumTijd {
  const opMinuut = Math.floor(nu / 60_000) * 60_000
  return delenVan(naarBovenOp5(opMinuut + STANDAARD_VOORUIT_MINUTEN * 60_000), zone)
}

/** Voor een `datetime-local`-veld: "2026-10-06T09:50". */
export function alsInvoerWaarde(m: DatumTijd): string {
  return `${m.datum}T${m.tijd}`
}

/** Het moment van een slot in UTC-milliseconden, of null als het niet klopt. */
export function slotMs(datum: string, tijd: string, zone: string = AMSTERDAM): number | null {
  const iso = zoneNaarUtc(datum, tijd, zone)
  if (!iso) return null
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Een snelknop "+15 min", "+30 min" of "+1 hour".
 *
 * Gekozen gedrag (04-10-2026): staat er in het slot een geldig moment dat nog
 * te plannen is, dan schuift de knop DAT moment op (20:30 → 20:45). Je kunt
 * dus een paar keer klikken om verder te schuiven. Is het slot leeg, ongeldig
 * of al voorbij, dan telt de knop vanaf nu, naar boven afgerond op 5 minuten
 * ("+15 min" om 12:03 geeft 12:20). Zo geeft een knop nooit een tijd in het
 * verleden.
 */
export function plusMinuten(
  datum: string, tijd: string, minuten: number, nu: number = Date.now(), zone: string = AMSTERDAM,
): DatumTijd {
  const huidig = slotMs(datum, tijd, zone)
  if (huidig != null && huidig >= nu + MIN_VOORUIT_MS) {
    return delenVan(huidig + minuten * 60_000, zone)
  }
  return delenVan(naarBovenOp5(Math.max(nu + minuten * 60_000, nu + MIN_VOORUIT_MS)), zone)
}

/** De tijden van de keuzelijst: elk kwartier van de dag ("00:00" … "23:45"). */
export function kwartierTijden(): string[] {
  const uit: string[] = []
  for (let m = 0; m < 24 * 60; m += 15) {
    uit.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
  }
  return uit
}

/** Is deze tijd op deze dag te kiezen (minstens 2 minuten vooruit)? */
export function tijdKiesbaar(datum: string, tijd: string, nu: number = Date.now(), zone: string = AMSTERDAM): boolean {
  const ms = slotMs(datum, tijd, zone)
  return ms != null && ms >= nu + MIN_VOORUIT_MS && ms <= nu + MAX_VOORUIT_MS
}

/** Wat er mis is met een gekozen moment; null = in orde. */
export function tijdFout(datum: string, tijd: string, nu: number = Date.now(), zone: string = AMSTERDAM): string | null {
  if (!datum || !tijd) return 'Pick a date and time.'
  const ms = slotMs(datum, tijd, zone)
  if (ms == null) return 'That date or time is not valid.'
  if (ms < nu + MIN_VOORUIT_MS) return TIJD_VOORBIJ
  if (ms > nu + MAX_VOORUIT_MS) return 'Cannot schedule more than 90 days ahead.'
  return null
}
